import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import type { Discipline } from '@structural-climbing/validation'
import { pressable } from '../../design/motion'
import { Skeleton } from '../../design/Skeleton'
import { useStandings } from './useStandings'
import { rankMessage } from './MyRankBanner'
import { ordinal } from './standingsUi'

interface Props {
  gymId: string
  discipline: Discipline
  onOpen: () => void
}

/**
 * Your place on the board, at the top of the wall. Tapping it opens the
 * leaderboard.
 *
 * This replaced the Routes / Standings bar above the tab bar, which members
 * read as a second tab bar and missed. Here the rank is the reason to tap.
 *
 * It asks for the all-time, everyone slice: the one the leaderboard opens on,
 * so opening the sheet straight after costs no second request.
 *
 * With no rank to show (a private account, nothing logged yet, or a failed
 * request) it still renders, as a plain way into the leaderboard.
 */
export function WallRankCard({ gymId, discipline, onOpen }: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const { data, loading } = useStandings(
    gymId,
    apiUrl,
    getAccessTokenSilently,
    'all',
    'all',
    discipline,
  )

  if (loading && !data) return <Skeleton className="h-[68px] rounded-lg" />

  const me = data?.myEntry ?? null

  return (
    <motion.button
      type="button"
      onClick={onOpen}
      {...pressable}
      data-testid="wall-rank-card"
      className="flex w-full items-center gap-3 rounded-lg bg-foreground px-4 py-3 text-left text-background shadow-e2"
    >
      {me ? (
        <>
          <span className="shrink-0 text-2xl font-extrabold tabular-nums tracking-tight">
            #{me.rank}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold">
              {t('standings.myRankTitle', { place: ordinal(me.rank, i18n.language) })}
            </span>
            <span className="block text-xs text-background/70">
              {rankMessage(t, me, data!.standings)}
            </span>
          </span>
        </>
      ) : (
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">{t('standings.pageTitle')}</span>
          <span className="block text-xs text-background/70">{t('standings.wallCardCta')}</span>
        </span>
      )}
      <span className="flex shrink-0 items-center gap-0.5 text-xs font-semibold text-background/85">
        {me && t('standings.menuLink')}
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </span>
    </motion.button>
  )
}
